// @flow

import invariant from 'invariant';
import camelCase from 'lodash/camelCase';
import snakeCase from 'lodash/snakeCase';

/**
 * Determines whether we can traverse the object
 */
export function canTraverseObject(obj: any) {
  if (obj !== Object(obj) || typeof obj === 'function') {
    return false;
  }
  const str = Object.prototype.toString.call(obj);
  if (str === '[object Date]' || str === '[object RegExp]' || str === '[object Boolean]') {
    return false;
  }
  return true;
}

/**
 * Processes an object to create a new one
 */
export function traverseObject(
  obj: Object,
  processKey: ?(string, ?any, ?any) => string = null,
  processValue: (any, string, any) => any = (value) => traverseObject(value, processKey)
) {
  if (!canTraverseObject(obj)) {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map((value, i) => processValue(value, i, obj));
  }

  const result = {};
  Object.entries(obj).forEach(([key, value]) => {
    const processedKey: string = processKey ? processKey(key, value, obj) : key;
    const processedValue = processValue ? processValue(value, key, obj) : value;
    result[processedKey] = processedValue;
  });
  return result;
}

/**
 * Creates a function that can recursively process the keys of an object
 */
export default function createKeyProcessor(
  processKeyAndPath: (string, Array<string>, any, any) => string,
  shouldProcessPathValue: (Array<string>) => boolean = () => true
) {
  invariant(
    typeof processKeyAndPath === 'function',
    'createKeyProcessor requires a function to process keys'
  );
  return (response: any) => {
    const pathParts = [];
    const processKey = (key: string, value: any, parent: any) =>
      processKeyAndPath(key, pathParts, value, parent);
    const processValue = (value: any, key: string) => {
      pathParts.push(key);
      const result =
        shouldProcessPathValue && shouldProcessPathValue(pathParts)
          ? traverseObject(value, processKey, processValue)
          : value;
      pathParts.pop();
      return result;
    };
    return traverseObject(response, processKey, processValue);
  };
}

/**
 * Replacement for humps
 */
export const camelizeKeys = createKeyProcessor(camelCase);
export const decamelizeKeys = createKeyProcessor(snakeCase);
